import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import { Checkbox } from '@/components/ui-kit/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { useCreateLeaveCategory, useUpdateLeaveCategory } from '../../hooks/use-leave-categories';
import {
  leaveCategoryFormSchema,
  LeaveCategoryFormValues,
} from '../../schemas/leave-category-form-schema';
import { LeaveCategory } from '../../types/leave-category.types';
import { isSystemLeaveCategory } from '../../utils/leave-category-rules';

export interface LeaveCategoryFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leaveCategory?: LeaveCategory | null;
}

export const LeaveCategoryFormDialog = ({
  open,
  onOpenChange,
  leaveCategory,
}: LeaveCategoryFormDialogProps) => {
  const { t } = useTranslation();
  const isEditMode = Boolean(leaveCategory);
  // The workflows find Sick Leave and Overtime Leave by title: their name stays.
  const isTitleLocked = isEditMode && isSystemLeaveCategory(leaveCategory?.Title);

  const { mutateAsync: createLeaveCategory, isPending: isCreating } = useCreateLeaveCategory();
  const { mutateAsync: updateLeaveCategory, isPending: isUpdating } = useUpdateLeaveCategory();
  const isSubmitting = isCreating || isUpdating;

  const form = useForm<LeaveCategoryFormValues>({
    resolver: async (data, context, options) => {
      const result = await zodResolver(leaveCategoryFormSchema)(data, context, options);
      if (result.errors) {
        result.errors = Object.fromEntries(
          Object.entries(result.errors).map(([key, error]) => [
            key,
            { ...error, message: t((error as { message?: string }).message ?? '') },
          ])
        );
      }
      return result;
    },
    defaultValues: {
      Title: '',
      LeaveLimit: 0,
      IsUnlimited: false,
    },
  });

  useEffect(() => {
    if (!open) return;
    form.reset({
      Title: leaveCategory?.Title ?? '',
      LeaveLimit: leaveCategory?.LeaveLimit ?? 0,
      IsUnlimited: leaveCategory?.IsUnlimited ?? false,
    });
  }, [open, leaveCategory, form]);

  const isUnlimited = form.watch('IsUnlimited');

  const handleSubmit = async (values: LeaveCategoryFormValues) => {
    try {
      if (isEditMode && leaveCategory) {
        await updateLeaveCategory({ itemId: leaveCategory.ItemId, input: values });
      } else {
        await createLeaveCategory(values);
      }
      onOpenChange(false);
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditMode ? t('EDIT_LEAVE_TYPE') : t('CREATE_LEAVE_TYPE')}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="Title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('LEAVE_TYPE_NAME')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('LEAVE_TYPE_NAME')} {...field} disabled={isTitleLocked} />
                  </FormControl>
                  {isTitleLocked && (
                    <p className="text-sm text-muted-foreground">{t('LEAVE_TYPE_SYSTEM_LOCKED')}</p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="IsUnlimited"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center gap-2 space-y-0">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <FormLabel className="!mt-0">{t('UNLIMITED_LEAVE')}</FormLabel>
                </FormItem>
              )}
            />

            {!isUnlimited && (
              <FormField
                control={form.control}
                name="LeaveLimit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('LEAVE_LIMIT_DAYS')}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        {...field}
                        onChange={(e) => field.onChange(e.target.valueAsNumber)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <DialogFooter className="gap-2 sm:gap-0">
              <DialogCancelButton onClick={() => onOpenChange(false)}>
                {t('CANCEL')}
              </DialogCancelButton>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
                {isEditMode ? t('SAVE_CHANGES') : t('CREATE')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
